import React, { useState, lazy } from 'react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { boats, Boat, BoatCategory } from '../data/boats';
import { getWhatsAppUrl } from '../utils/whatsapp';
import { MessageCircle, AlertTriangle, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
export const FleetCatalog = () => {
  const {
    t
  } = useLanguage();
  const [activeFilter, setActiveFilter] = useState<BoatCategory | 'all'>('all');
  const filters: {
    id: BoatCategory | 'all';
    labelKey: string;
  }[] = [{
    id: 'all',
    labelKey: 'fleet.filter.all'
  }, {
    id: 'sin-licencia',
    labelKey: 'fleet.filter.noLicense'
  }, {
    id: 'con-licencia',
    labelKey: 'fleet.filter.license'
  }];
  const filteredBoats = activeFilter === 'all' ? boats : boats.filter(b => b.category === activeFilter);
  const handleWhatsApp = (boat: Boat) => {
    const templateKey = boat.waTemplateKey ?? 'wa.general';
    const message = t(templateKey).replace('[BARCO]', boat.name).replace('[FECHA]', '___').replace('[NÚMERO]', '___').replace('[SÍ/NO]', '___').replace('[PRESUPUESTO]', '___');
    window.open(getWhatsAppUrl(message), '_blank');
  };
  return <section id="barcos" className="py-12 sm:py-16 md:py-20 bg-white" data-id="element-133">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-134">
        <div className="text-center mb-8 sm:mb-12" data-id="element-135">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-navy mb-6 sm:mb-8" data-id="element-136">
            {t('fleet.title')}
          </h2>

          <div className="flex flex-wrap justify-center gap-2 md:gap-4" data-id="element-137">
            {filters.map(filter => <button key={filter.id} onClick={() => setActiveFilter(filter.id)} className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-bold transition-all ${activeFilter === filter.id ? 'bg-navy text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-138">
                {t(filter.labelKey)}
              </button>)}
          </div>
        </div>

        {/* Boat Grid — 3 desktop / 2 tablet / 1 mobile */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-7" data-id="element-139">
          {filteredBoats.map(boat => <BoatCard key={boat.id} boat={boat} onWhatsApp={() => handleWhatsApp(boat)} t={t} data-id="element-140" />)}
        </div>
      </div>
    </section>;
};
type BoatCardProps = {
  boat: Boat;
  onWhatsApp: () => void;
  t: (key: string) => string;
};
const BoatCard = ({
  boat,
  onWhatsApp,
  t
}: BoatCardProps) => {
  const photos = boat.gallery && boat.gallery.length > 0 ? boat.gallery : [boat.image];
  const hasGallery = photos.length > 1;
  const [index, setIndex] = useState(0);
  const advance = (dir: 1 | -1) => {
    setIndex(prev => (prev + dir + photos.length) % photos.length);
  };
  const handleDragEnd = (_e: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const threshold = 50;
    if (info.offset.x > threshold) advance(-1);else if (info.offset.x < -threshold) advance(1);
  };
  const bannerKey = boat.licenseBanner ?? (boat.specs.licenseReq ? 'boat.banner.licenseReq' : 'boat.banner.noLicense');
  const banner = t(bannerKey);
  return <article className="rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 flex flex-col h-full bg-white" data-id="element-141">
      {/* TOP HALF — PHOTO carousel with name overlay */}
      <div className="relative aspect-[4/3] overflow-hidden group" data-id="element-142">
        <AnimatePresence mode="wait" initial={false} data-id="element-143">
          <motion.img key={photos[index]} src={photos[index]} alt={`${boat.name}${hasGallery ? ` — foto ${index + 1} de ${photos.length}` : ''}`} loading="lazy" decoding="async" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} drag={hasGallery ? 'x' : false} dragConstraints={{
          left: 0,
          right: 0
        }} dragElastic={0.2} onDragEnd={hasGallery ? handleDragEnd : undefined} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 cursor-grab active:cursor-grabbing" data-id="element-144" />
        </AnimatePresence>

        {/* Bottom gradient for text legibility */}
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none" data-id="element-145" />

        {/* Carousel controls */}
        {hasGallery && <>
            <button type="button" onClick={e => {
          e.stopPropagation();
          advance(-1);
        }} aria-label={t('premium.aria.prev')} className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 backdrop-blur-md text-white rounded-full p-2 transition-all hover:scale-110 shadow-lg" data-id="element-146">
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-147" />
            </button>
            <button type="button" onClick={e => {
          e.stopPropagation();
          advance(1);
        }} aria-label={t('premium.aria.next')} className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 backdrop-blur-md text-white rounded-full p-2 transition-all hover:scale-110 shadow-lg" data-id="element-148">
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-149" />
            </button>

            {/* Dots */}
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex gap-1.5 pointer-events-none" data-id="element-150">
              {photos.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === index ? 'bg-white w-5' : 'bg-white/50 w-1.5'}`} data-id="element-151" />)}
            </div>
          </>}

        {/* Boat name */}
        <h3 className="absolute left-4 right-4 bottom-3 sm:bottom-4 text-white font-black uppercase leading-none text-2xl sm:text-3xl md:text-4xl pointer-events-none z-10" style={{
        letterSpacing: '0.12em'
      }} data-id="element-152">
          {boat.name}
        </h3>
      </div>

      {/* BOTTOM HALF — INFO PANEL */}
      <div className="bg-gradient-to-r from-cyan-500 to-emerald-400 text-white flex-1 flex flex-col" data-id="element-153">
        {/* License banner */}
        <div className="text-center py-2.5 px-3 border-b border-white/30" data-id="element-154">
          <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.15em]" data-id="element-155">
            {banner}
          </span>
        </div>

        {/* Content */}
        <div className="grid grid-cols-[auto_1fr] gap-x-4 sm:gap-x-5 px-4 sm:px-5 py-5 flex-1 items-center" data-id="element-156">
          {/* Left: specs */}
          <div className="border-r border-white/40 pr-4 sm:pr-5 text-[11px] sm:text-xs font-bold uppercase tracking-wider leading-snug space-y-1.5" data-id="element-157">
            {boat.specLines ? boat.specLines.map((line, i) => <div key={i} data-id="element-158">{t(line)}</div>) : <>
                <div className="whitespace-nowrap" data-id="element-159">
                  {boat.specs.capacity} {t('boat.spec.peopleMax')}
                </div>
                {boat.specs.length && <div className="whitespace-nowrap" data-id="element-160">
                    {boat.specs.length} {t('boat.spec.length')}
                  </div>}
              </>}
          </div>

          {/* Right: prices */}
          <div className="space-y-1.5 sm:space-y-2" data-id="element-161">
            {boat.prices.map((p, idx) => <div key={idx} className="flex items-baseline justify-between gap-3 font-bold" data-id="element-162">
                <span className="text-xs sm:text-sm tracking-wider" data-id="element-163">
                  {t(p.label)}
                </span>
                <span className="text-lg sm:text-xl tracking-tight" data-id="element-164">
                  {p.price}
                </span>
              </div>)}
          </div>
        </div>

        {/* Duration equivalence — shown on every card */}
        <div className="mx-4 sm:mx-5 mb-3 flex items-center justify-center gap-1.5 text-white/90 text-[10px] sm:text-[11px] font-bold uppercase tracking-wide" data-id="element-165">
          <Clock className="w-3.5 h-3.5 flex-shrink-0" data-id="element-166" />
          <span data-id="element-167">{t('boat.durationInfo')}</span>
        </div>

        {/* Optional note */}
        {boat.noteText && <div className="mx-4 sm:mx-5 mb-4 flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 rounded-lg px-3 py-2 text-[11px] sm:text-xs font-semibold" data-id="element-168">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" data-id="element-169" />
            <span data-id="element-170">{t(boat.noteText)}</span>
          </div>}
      </div>

      {/* CTA — attached at the bottom of the card */}
      <button onClick={onWhatsApp} className="bg-navy hover:bg-navy-light text-white font-bold py-3.5 px-4 transition-colors flex items-center justify-center space-x-2 uppercase text-xs sm:text-sm tracking-wider" data-id="element-171">
        <MessageCircle className="w-5 h-5 text-turquoise" data-id="element-172" />
        <span data-id="element-173">{t('boat.cta')}</span>
      </button>
    </article>;
};